import React from 'react';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/partials/Breadcrumb';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <DashboardLayout>
      <div className="px-4 py-5 md:px-10 md:py-8">
        <div className="mx-auto max-w-3xl">
          <Breadcrumb current="Privacy Policy" />

          <div className="rounded-md border border-theme-border bg-surface px-4 py-8 shadow sm:px-8 sm:py-10">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-on-surface-tertiary">
              Digital Ascension Group
            </p>
            <h1 className="mb-2 text-2xl font-bold text-on-surface sm:text-3xl">
              Privacy Policy
            </h1>
            <p className="mb-8 text-sm text-on-surface-tertiary">
              Effective date: April 7, 2026
            </p>

            <div className="space-y-8 text-sm leading-relaxed text-on-surface-secondary">
              <section className="space-y-3">
                <p>
                  Digital Ascension Group (&ldquo;DAG,&rdquo; &ldquo;we,&rdquo;
                  &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your privacy
                  and is committed to protecting the personal information you
                  share with us. This Privacy Policy explains how we collect,
                  use, disclose, and safeguard your information when you use our
                  website, platform, and related services (collectively, the
                  &ldquo;Services&rdquo;).
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  1. Information We Collect
                </h2>
                <p>
                  We collect information that you provide directly to us, such
                  as when you create an account, complete a profile, request
                  services, or communicate with us. This may include:
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    Identity information (name, date of birth, citizenship,
                    government-issued ID)
                  </li>
                  <li>
                    Contact information (email address, phone number, mailing
                    address)
                  </li>
                  <li>
                    Business information (entity name, formation details,
                    ownership structure)
                  </li>
                  <li>
                    Financial information (payment details, banking information,
                    transaction history)
                  </li>
                  <li>Documents and records you upload to the platform</li>
                </ul>
                <p>
                  We also automatically collect certain information when you use
                  the Services, including device identifiers, IP address,
                  browser type, pages visited, and usage activity through
                  cookies and similar technologies.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  2. How We Use Your Information
                </h2>
                <p>We use the information we collect to:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Provide, operate, and maintain the Services</li>
                  <li>
                    Process transactions and facilitate business formation,
                    banking, and related services
                  </li>
                  <li>
                    Verify your identity and comply with KYC, AML, and other
                    legal requirements
                  </li>
                  <li>
                    Communicate with you about your account, requests, and
                    service updates
                  </li>
                  <li>Improve, personalize, and develop the Services</li>
                  <li>
                    Detect, investigate, and prevent fraudulent or unauthorized
                    activity
                  </li>
                  <li>Comply with applicable laws and legal obligations</li>
                </ul>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  3. How We Share Your Information
                </h2>
                <p>
                  We do not sell your personal information. We may share your
                  information with:
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    Service providers and third-party vendors who perform
                    services on our behalf (including payment processors,
                    banking partners, identity verification providers, and
                    document signing services)
                  </li>
                  <li>
                    Government agencies and regulators, where required by law or
                    to facilitate business formation and compliance
                  </li>
                  <li>
                    Professional advisors, such as legal counsel and auditors
                  </li>
                  <li>
                    Other parties in connection with a corporate transaction,
                    such as a merger, acquisition, or sale of assets
                  </li>
                  <li>
                    Other parties when you direct us to share your information
                    or consent to such sharing
                  </li>
                  <li>
                    Some financial services and transactions made available
                    through this platform are provided by Uphold. For details on
                    how Uphold collects, uses, and protects your personal data,
                    please refer to their{'  '}
                    <a
                      href="https://uphold.com/en-us/legal/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 underline underline-offset-2 hover:text-primary-700"
                    >
                      Privacy Notice
                    </a>
                    .
                  </li>
                </ul>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  4. Data Security
                </h2>
                <p>
                  We implement reasonable administrative, technical, and
                  physical safeguards designed to protect your information.
                  However, no method of transmission over the Internet or
                  electronic storage is completely secure, and we cannot
                  guarantee absolute security.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  5. Data Retention
                </h2>
                <p>
                  We retain your personal information for as long as necessary
                  to provide the Services, comply with our legal obligations,
                  resolve disputes, and enforce our agreements. Retention
                  periods may vary depending on the nature of the information
                  and applicable legal requirements.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  6. Your Rights and Choices
                </h2>
                <p>
                  Depending on your jurisdiction, you may have certain rights
                  regarding your personal information, including the right to
                  access, correct, delete, or restrict the processing of your
                  information. You may also have the right to object to certain
                  processing or to withdraw your consent. To exercise these
                  rights, please contact us using the information below.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  7. Cookies and Tracking Technologies
                </h2>
                <p>
                  We use cookies and similar tracking technologies to operate
                  and improve the Services, remember your preferences, and
                  analyze usage. You can control cookies through your browser
                  settings, but disabling them may affect certain features of
                  the Services.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  8. Children&apos;s Privacy
                </h2>
                <p>
                  The Services are not directed to children under the age of 18,
                  and we do not knowingly collect personal information from
                  children. If you believe we have collected information from a
                  child, please contact us so we can take appropriate action.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  9. International Users
                </h2>
                <p>
                  The Services are operated in the United States. If you access
                  the Services from outside the United States, you understand
                  that your information may be transferred to, stored, and
                  processed in the United States, where data protection laws may
                  differ from those in your jurisdiction.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  10. Changes to This Privacy Policy
                </h2>
                <p>
                  We may update this Privacy Policy from time to time. When we
                  make material changes, we will notify you by posting the
                  updated policy on our platform and updating the effective date
                  above. Your continued use of the Services after such changes
                  constitutes your acceptance of the updated policy.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  11. Contact Us
                </h2>
                <p>
                  If you have any questions about this Privacy Policy or our
                  privacy practices, please contact us at{' '}
                  <a
                    href="mailto:support@dag.com"
                    className="text-primary-600 underline underline-offset-2 hover:text-primary-700"
                  >
                    support@dag.com
                  </a>
                  .
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PrivacyPolicyPage;
